import { useCallback, useEffect, useMemo, useState } from "react";
import api, { fmtTRY } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Plus, Search, FileSpreadsheet } from "lucide-react";
import ExpenseBudgets from "../components/expenses/ExpenseBudgets";
import { ExpenseStats, ExpenseCategoryChart } from "../components/expenses/ExpenseSummary";
import ExpenseFormDialog from "../components/expenses/ExpenseFormDialog";
import ExpenseList from "../components/expenses/ExpenseList";
import RecurringExpenses from "../components/expenses/RecurringExpenses";
import { METHOD } from "../components/expenses/constants";
import { useAuth } from "../lib/auth";
import ExpenseEntryOnly from "../components/expenses/ExpenseEntryOnly";
import DatePicker from "../components/common/DatePicker";

export default function ExpensesPage() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || (user?.permissions || []).includes("expenses_view");
  return canManage ? <ExpensesManager /> : <ExpenseEntryOnly />;
}

function ExpensesManager() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [recurring, setRecurring] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [cats, setCats] = useState({ defaults: [], custom: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ q: "", category: "all", method: "all", from: "", to: "" });

  const loadCats = useCallback(() => api.get("/expense-categories").then((r) => setCats(r.data)), []);
  const load = useCallback(async () => {
    const s = await api.get("/expenses/summary");
    setSummary(s.data);
    const [t, r, b] = await Promise.all([api.get("/expenses"), api.get("/recurring-expenses"), api.get("/expense-budgets")]);
    setItems(t.data.sort((a, b) => (b.date || "").localeCompare(a.date || ""))); setRecurring(r.data); setBudgets(b.data);
  }, []);
  useEffect(() => { load(); loadCats(); }, [load, loadCats]);

  const categories = useMemo(() => [...cats.defaults, ...cats.custom.map((c) => c.name)], [cats]);
  const filtered = useMemo(() => items.filter((t) => {
    const d = (t.date || "").slice(0, 10);
    if (f.category !== "all" && t.category !== f.category) return false;
    if (f.method !== "all" && t.payment_method !== f.method) return false;
    if (f.from && d < f.from) return false;
    if (f.to && d > f.to) return false;
    const q = f.q.toLocaleLowerCase("tr");
    return !q || `${t.category} ${t.description}`.toLocaleLowerCase("tr").includes(q);
  }), [items, f]);
  const filteredTotal = filtered.reduce((s, t) => s + Number(t.amount || 0), 0);

  const openNew = () => { setEditing(null); setOpen(true); };
  const onEdit = (t) => { setEditing(t); setOpen(true); };
  const onDelete = async (id) => {
    if (!window.confirm("Gider silinsin mi?")) return;
    await api.delete(`/expenses/${id}`); toast.success("Silindi"); load();
  };
  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = { q: f.q || undefined, category: f.category !== "all" ? f.category : undefined, method: f.method !== "all" ? f.method : undefined, date_from: f.from || undefined, date_to: f.to || undefined };
      const { data } = await api.get("/expenses/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `giderler_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel indirildi");
    } catch { toast.error("Excel oluşturulamadı"); }
    setExporting(false);
  };
  const setFilter = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-5" data-testid="expenses-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Muhasebe</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Giderler</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" disabled={exporting} onClick={exportExcel} data-testid="export-expenses-btn"><FileSpreadsheet size={16} className="text-emerald-700" /> {exporting ? "Hazırlanıyor..." : "Excel"}</Button>
          <Button className="bg-rose-600 hover:bg-rose-700 text-white gap-2" onClick={openNew} data-testid="add-expense-btn"><Plus size={16} /> Gider Ekle</Button>
        </div>
      </div>

      <ExpenseStats summary={summary} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ExpenseCategoryChart summary={summary} />
        <ExpenseBudgets budgets={budgets} categories={categories} onChanged={load} />
      </div>
      <RecurringExpenses items={recurring} onChanged={load} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="exp-filters">
        <div className="relative col-span-2 md:col-span-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-8" placeholder="Ara..." value={f.q} onChange={(e) => setFilter("q")(e.target.value)} data-testid="exp-filter-search" />
        </div>
        <Select value={f.category} onValueChange={setFilter("category")}>
          <SelectTrigger data-testid="exp-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm Kategoriler</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.method} onValueChange={setFilter("method")}>
          <SelectTrigger data-testid="exp-filter-method"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm Ödemeler</SelectItem>{Object.entries(METHOD).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <DatePicker clearable placeholder="Başlangıç" value={f.from} onChange={setFilter("from")} testid="exp-filter-from" />
        <DatePicker clearable placeholder="Bitiş" value={f.to} onChange={setFilter("to")} testid="exp-filter-to" />
      </div>
      <div className="flex justify-between text-sm text-slate-600">
        <span>{filtered.length} kayıt</span>
        <span>Filtre toplamı: <b className="text-rose-600" data-testid="exp-filtered-total">{fmtTRY(filteredTotal)}</b></span>
      </div>

      <ExpenseList items={filtered} onEdit={onEdit} onDelete={onDelete} />

      <ExpenseFormDialog open={open} onOpenChange={setOpen} editing={editing} categories={categories} onCategoriesChanged={loadCats} onSaved={load} />
    </div>
  );
}
